/**
 * Discussion composer.
 *
 * Owns the draft text and the `@` mention picker so the box for a new message and
 * the box for an inline reply behave identically. The picker is driven by the
 * caret position rather than by parsing the whole message: typing `@` after a
 * boundary opens the list, and choosing a name replaces that partial mention with
 * the full nickname.
 *
 * The component never talks to the API: the page decides what a submitted string
 * means, which keeps one mutation path for both entry points.
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LIMITS } from '@shared/constants'

import { ErrorAlert } from '../layout/states'
import { Button } from '../ui/button'
import { Textarea } from '../ui/input'
import { mutationErrorText } from '../../lib/mutation-error'

/** Matches a partial `@name` immediately before the caret. */
const MENTION_PATTERN = /(?:^|\s)@([^\s@]*)$/
/** How many mention suggestions the picker shows at once. */
const MENTION_LIMIT = 6

export interface MentionCandidate {
  id: string
  nickname: string
}

export interface CommentComposerProps {
  /** Participants the `@` picker may suggest. */
  candidates: readonly MentionCandidate[]
  placeholder: string
  submitLabel: string
  pendingLabel: string
  isPending: boolean
  error: Error | null
  /**
   * Sends the message. The box is cleared only once the returned promise settles
   * successfully, so a failed send keeps the text for another try.
   */
  onSubmit: (content: string) => void | Promise<unknown>
  /** Present only for an inline reply, which can be dismissed. */
  onCancel?: () => void
  autoFocus?: boolean
}

export function CommentComposer({
  candidates,
  placeholder,
  submitLabel,
  pendingLabel,
  isPending,
  error,
  onSubmit,
  onCancel,
  autoFocus,
}: CommentComposerProps) {
  const { t } = useTranslation()
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const caretRef = useRef(0)
  const [content, setContent] = useState('')
  const [mentionQuery, setMentionQuery] = useState<string | null>(null)

  useEffect(() => {
    if (!autoFocus) {
      return
    }
    // The reply box opens under the message the member clicked, so the caret has
    // to follow it there instead of staying on the message.
    const node = textareaRef.current
    if (!node) {
      return
    }
    node.focus()
    node.setSelectionRange(0, 0)
  }, [autoFocus])

  function syncMention(value: string, caret: number): void {
    caretRef.current = caret
    const match = MENTION_PATTERN.exec(value.slice(0, caret))
    setMentionQuery(match ? (match[1] ?? '') : null)
  }

  function insertMention(nickname: string): void {
    const caret = caretRef.current
    const before = content.slice(0, caret)
    const replaced = before.replace(MENTION_PATTERN, (whole) => {
      const prefix = whole.startsWith('@') ? '' : whole.charAt(0)
      return `${prefix}@${nickname} `
    })
    const next = replaced + content.slice(caret)
    setContent(next)
    setMentionQuery(null)
    window.requestAnimationFrame(() => {
      const node = textareaRef.current
      if (!node) {
        return
      }
      node.focus()
      node.setSelectionRange(replaced.length, replaced.length)
    })
  }

  async function send(): Promise<void> {
    const trimmed = content.trim()
    if (trimmed === '' || isPending) {
      return
    }
    try {
      await onSubmit(trimmed)
    } catch {
      // The caller already renders the failure; the text stays where it is so the
      // member can send it again instead of retyping it.
      return
    }
    setContent('')
    setMentionQuery(null)
  }

  const mentionOptions =
    mentionQuery === null
      ? []
      : candidates
          .filter((candidate) =>
            candidate.nickname.toLowerCase().includes(mentionQuery.toLowerCase()),
          )
          .slice(0, MENTION_LIMIT)

  return (
    <div className="flex flex-col gap-3">
      <Textarea
        ref={textareaRef}
        value={content}
        placeholder={placeholder}
        maxLength={LIMITS.commentMaxLength}
        onChange={(event) => {
          setContent(event.target.value)
          syncMention(event.target.value, event.target.selectionStart ?? event.target.value.length)
        }}
        onSelect={(event) =>
          syncMention(content, event.currentTarget.selectionStart ?? content.length)
        }
      />

      {mentionOptions.length > 0 ? (
        <div className="border-border flex flex-col gap-1 rounded-md border p-2">
          <span className="text-muted-foreground text-xs">{t('discuss.mentionEmpty')}</span>
          <div className="flex flex-wrap gap-1.5">
            {mentionOptions.map((candidate) => (
              <Button
                key={candidate.id}
                variant="outline"
                size="sm"
                onClick={() => insertMention(candidate.nickname)}
              >
                {candidate.nickname}
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      {error ? (
        <ErrorAlert message={mutationErrorText(error, t)} />
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button disabled={isPending || content.trim() === ''} onClick={() => void send()}>
          {isPending ? pendingLabel : submitLabel}
        </Button>
        {onCancel ? (
          <Button variant="ghost" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        ) : null}
      </div>
    </div>
  )
}
